// Imported by vite.config.ts, so: relative imports only, no `@/` aliases or config.

/**
 * Publication gate for the competitor pages. Owner 2026-10-28: open now, no lawyer review (D7
 * dropped); every "Yes" in our columns was checked against released features (D1, PR description).
 * Set false to answer 404 and drop the pages from prerender, the sitemap and llms.txt.
 */
export const COMPARE_PUBLISHED = true

export const COMPARE_PATHS = [
  "/compare",
  "/compare/driversnote-alternative",
  "/compare/spreadsheet-logbook",
] as const

/** Pages that are prerendered, listed in the sitemap and indexable. */
export const STATIC_SITEMAP_PATHS: readonly string[] = [
  "/",
  "/blog",
  "/pricing",
  "/faq",
  "/trust",
  "/contact",
  "/how-it-works",
  "/invoice-generator",
  "/rideshare-drivers",
  "/glossary",
  "/guides",
  "/ato-logbook-template",
  "/logbook-method",
  "/cents-per-km-calculator",
  "/gst-calculator",
  "/pay-calculator",
  "/sole-trader-tax-calculator",
  "/hecs-repayment-calculator",
  "/medicare-levy-calculator",
  "/mileage-tracker",
  "/receipt-scanner",
  "/invoicing",
  // Competitor pages: see COMPARE_PUBLISHED above
  ...(COMPARE_PUBLISHED ? COMPARE_PATHS : []),
]

/**
 * Pages that also exist in Chinese (D-WEB-LANG-SCOPE, T1256/T1257), as English paths: the Chinese
 * version lives at `/zh` + path (`/zh` for the home page). The one list behind hreflang, the
 * language switcher, link localisation, prerender and the sitemap. Add a page here only together
 * with its route in routes.ts and its Chinese copy; grow it as pages are translated.
 */
export const ZH_PAGE_PATHS: readonly string[] = [
  "/",
  "/mileage-tracker",
  "/receipt-scanner",
  "/invoicing",
  "/pricing",
  "/how-it-works",
  "/faq",
  "/trust",
  "/rideshare-drivers",
  "/contact",
  "/cents-per-km-calculator",
  "/gst-calculator",
  "/medicare-levy-calculator",
  "/hecs-repayment-calculator",
  "/pay-calculator",
  "/sole-trader-tax-calculator",
  "/ato-logbook-template",
  "/invoice-generator",
]

/** Chinese URL path of a path in `ZH_PAGE_PATHS` (this file imports nothing, see top). */
export const zhPagePath = (path: string): string => (path === "/" ? "/zh" : `/zh${path}`)

export const ZH_SITEMAP_PATHS: readonly string[] = ZH_PAGE_PATHS.map(zhPagePath)

/** Prerendered but kept out of the index with a `noindex` meta tag (not a robots Disallow). */
export const NOINDEX_PRERENDER_PATHS: readonly string[] = ["/billing/success", "/waitlist"]

/** Dynamic-path sections; each slug is added to `prerender.pages` in vite.config.ts. */
const PRERENDER_PREFIXES: readonly string[] = ["/blog/", "/legal/", "/glossary/", "/guides/"]

/** The one allow-list: vite `prerender.filter`, the sitemap and their tests all use it. */
export function isPrerenderPath(path: string): boolean {
  return (
    STATIC_SITEMAP_PATHS.includes(path) ||
    ZH_SITEMAP_PATHS.includes(path) ||
    NOINDEX_PRERENDER_PATHS.includes(path) ||
    PRERENDER_PREFIXES.some((prefix) => path.startsWith(prefix))
  )
}
